import React, { useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useNavigate, Link } from "react-router-dom";
import Loading from "../components/UI/Loading/Loading";
import { useEffect } from "react";
import { io } from "socket.io-client";
import Cookies from "js-cookie";
import axios from "axios";
import { queryClient } from "../App";
import chat_notification from "../assets/chat_notification.wav";
import notification_sweet from "../assets/notification_sweet.wav";
import { setOnlineUsers } from "../app/slices/soketSlice";
import { toast } from "react-hot-toast";
import Notification from "../components/UI/Notification/Notification";
import { logout, reciveNoti, updateNStats } from "../app/slices/userSlice";
import {
  updateChatName,
  updateChatTheme,
  updateMessages,
  updateSeenMessages,
} from "../utils/rqUpdate";
import { backendUrl, apiUrl } from "../utils/backendUrl";
import {
  getRealtimeMessageSenderId,
  isRealtimeMessageFromCurrentUser,
  shouldOpenMiniChatForMessage,
} from "../utils/miniChatRealtime";
import { getBrowserSessionMetadata } from "../services/deviceIdentity";
import Topbar from "../components/layout/Topbar";
import SubNavbar from "../components/layout/SubNavbar";
import Footer from "../components/layout/Footer";
import ChatSidebar from "../components/layout/ChatSidebar";
import ChatPopupManager from "../components/chat/ChatPopupManager";
import { MiniChatProvider, miniChatBus } from "../context/MiniChatContext";
import { createRingtone } from "../utils/ringtone";
import { queueSystemLog } from "../services/systemLogs";
import {
  clearClientSession,
  handleUnauthorized,
  logoutSession,
} from "../services/api";
import { store } from "../app/store";
import useAuthenticatedDropdowns from "./useAuthenticatedDropdowns";
import {
  classifyAcceptFailure,
  getCallerDisplayName,
  getRemainingRingMs,
  getSocketAuthErrorCode,
  sessionMatchesThisTab,
} from "./incomingCallUtils";

export let socket;
const socketSubscribers = new Set();

function notifySocketSubscribers() {
  socketSubscribers.forEach((subscriber) => subscriber(socket));
}

export function useAppSocket() {
  const [activeSocket, setActiveSocket] = useState(socket);

  useEffect(() => {
    socketSubscribers.add(setActiveSocket);
    setActiveSocket(socket);
    return () => socketSubscribers.delete(setActiveSocket);
  }, []);

  return activeSocket;
}

function getSocketAuthToken() {
  const rawToken = Cookies.get("token");
  if (!rawToken) return "";

  try {
    const parsedToken = JSON.parse(rawToken);
    return typeof parsedToken === "string" ? parsedToken : "";
  } catch {
    return rawToken;
  }
}

// Every "this session is no longer valid" signal (401 on a call action, a
// SOCKET_AUTH_* connect error, the server's auth:expired) ends the app
// session the same way: drop the store user, close the realtime socket and
// go through the API service's shared unauthorized path.
function performForcedLogout({ dispatch, socket: activeSocket, reason }) {
  queueSystemLog({
    level: "warn",
    category: "auth",
    event: "session.forced_logout",
    message: "Signed out after an authentication failure",
    details: { reason: reason || "" },
  });
  try {
    dispatch(logout());
  } catch (_) {}
  try {
    activeSocket?.disconnect?.();
  } catch (_) {}
  handleUnauthorized();
}

function playNotificationSound() {
  const audio = new Audio(chat_notification);
  audio.play();
}

function playNotificationSound2() {
  const audio = new Audio(notification_sweet);
  audio.play();
}

export default function IsLoggedIn({ user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const onlineUsers = useSelector((state) => state.soketSlice?.onlineUsers || []);
  const publicConfig = queryClient.getQueryData(["public-config"]);
  const callCapabilities = useMemo(
    () => ({
      multiDeviceCallsEnabled:
        publicConfig?.general?.multiDeviceCallsEnabled === true,
      callJoinHereTooEnabled:
        publicConfig?.general?.callJoinHereTooEnabled === true,
      callMoveHereEnabled:
        publicConfig?.general?.callMoveHereEnabled === true,
    }),
    [publicConfig]
  );

  const handleLogout = async () => {
    // Shared teardown (services/api.js): POST /auth/logout so the server
    // expires its httpOnly jwt cookie, close the socket, drop the bearer
    // header, the client cookies and the React Query cache.
    await logoutSession({ socket });
    dispatch(logout());
    // userSlice.logout() re-writes the auth cookies as the literal "null";
    // clear them once more so nothing survives for the next login.
    clearClientSession();
    navigate("/login");
  };

  const msgNoti = (newMessage) => {
    const res = window.location.pathname.split("/").pop() !== newMessage.chat;
    return res;
  };

  // ── Ringtone (looping ring for incoming calls) ─────────────────────
  const ringtoneRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null); // { callId, roomId, type, caller }
  const [secondaryCallOffer, setSecondaryCallOffer] = useState(null);
  const incomingCallRef = useRef(null);
  const callCapabilitiesRef = useRef(callCapabilities);
  // Call id this tab is currently accepting: the server fans
  // call:answered_elsewhere to the user's sessions the moment accept lands,
  // and this tab must not treat its own accept as "answered elsewhere".
  const acceptingCallIdRef = useRef(null);
  // Auto-dismiss timer for the ringing overlay (45s from initiatedAt).
  const ringTimeoutRef = useRef(null);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }
  }, []);

  const clearRingTimeout = useCallback(() => {
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
  }, []);

  const startRingtone = useCallback(() => {
    stopRingtone();
    ringtoneRef.current = createRingtone();
    ringtoneRef.current.start();
  }, [stopRingtone]);

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  useEffect(() => {
    callCapabilitiesRef.current = callCapabilities;
  }, [callCapabilities]);

  // Socket lifecycle intentionally binds once per authenticated page load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user.userinfo) {
      socket = io(backendUrl, {
        autoConnect: false,
        // Read the cookie on every (re)connect attempt so a token refreshed
        // since page load is used instead of the one captured at mount.
        auth: (cb) => cb({ token: getSocketAuthToken() }),
      });
      const mountedSocket = socket;
      notifySocketSubscribers();
      queueSystemLog({
        level: "info",
        category: "realtime",
        event: "socket.connection_created",
        message: "Web realtime connection created",
        details: { endpoint: backendUrl },
      });

      // Emit setup on every (re)connection so the server registers this
      // user as online even after server restarts / deploys.
      const emitSetup = () => {
        const userId = user.userinfo._id || user.userinfo.id;
        const session = getBrowserSessionMetadata();
        mountedSocket.emit("setup", {
          userId,
          ...session,
          info: {
            id: userId,
            _id: userId,
            guid: user.userinfo.guid ?? user.userinfo.legacyGuid ?? null,
            legacyGuid: user.userinfo.legacyGuid ?? user.userinfo.guid ?? null,
            username: user.userinfo.username,
            first_name: user.userinfo.first_name || user.userinfo.firstName,
            last_name: user.userinfo.last_name || user.userinfo.lastName,
            firstName: user.userinfo.firstName || user.userinfo.first_name,
            lastName: user.userinfo.lastName || user.userinfo.last_name,
            photo: user.userinfo.photo || user.userinfo.avatarUrl,
          },
        });
        queueSystemLog({
          level: "info",
          category: "realtime",
          event: "socket.connected",
          message: "Web realtime connection established",
          details: {
            socketId: mountedSocket.id || "",
            transport: mountedSocket.io?.engine?.transport?.name || "",
          },
        });
      };
      mountedSocket.on("connect", emitSetup);
      mountedSocket.on("connect_error", (error) => {
        const authErrorCode = getSocketAuthErrorCode(error);
        queueSystemLog({
          level: "error",
          category: "realtime",
          event: "socket.connection_error",
          message: error?.message || "Web realtime connection failed",
          details: {
            transport: mountedSocket.io?.engine?.transport?.name || "",
            active: mountedSocket.active,
            code: error?.data?.code || "",
          },
          error: {
            name: error?.name || "Error",
            message: error?.message || "Web realtime connection failed",
          },
        });
        // The server refused the session itself (expired / invalid token,
        // password changed, banned...): retrying with the same cookie can
        // never succeed, so stop reconnecting and end the app session.
        if (authErrorCode) {
          performForcedLogout({
            dispatch,
            socket: mountedSocket,
            reason: authErrorCode,
          });
        }
      });
      // Emitted right before the server force-disconnects an expired
      // session; without this the client would just reconnect and be
      // refused (see connect_error above).
      mountedSocket.on("auth:expired", (payload = {}) => {
        const code = `${payload?.code || "SOCKET_AUTH_EXPIRED"}`;
        queueSystemLog({
          level: "warn",
          category: "realtime",
          event: "socket.auth_expired",
          message: "Realtime session expired",
          details: { code },
        });
        performForcedLogout({ dispatch, socket: mountedSocket, reason: code });
      });

      socket.on("online_user", ({ type, info }) => {
        dispatch(setOnlineUsers({ type, info }));
      });

      socket.on("new_notification", ({ notification } = {}) => {
        playNotificationSound2();
        dispatch(
          reciveNoti({
            type: "notification",
          })
        );

        // If this is a friend request, also bump the friend-requests badge.
        // This handler is bound once per page load, so the `user` prop in
        // this closure is the login-time snapshot: read the live count from
        // the store instead.
        if (notification?.type === "friend_request") {
          const currentCount =
            Number(store.getState()?.user?.userinfo?.recivedRequestsCount) ||
            0;
          dispatch(updateNStats({ recivedRequestsCount: currentCount + 1 }));
        }

        toast.custom(
          (t) => (
            <Notification t={t} toast={toast} notification={notification} />
          ),
          { duration: 8000 }
        );

        // Immutable update that keeps the consumer's shape
        // ({ data: { notifications: [...] } }) instead of mutating in place.
        queryClient.setQueryData(["getNotifications"], (oldData) => {
          if (!oldData?.data || !notification) return oldData;
          const existing = Array.isArray(oldData.data.notifications)
            ? oldData.data.notifications
            : [];
          return {
            ...oldData,
            data: {
              ...oldData.data,
              notifications: [notification, ...existing],
            },
          };
        });
      });

      // When a friend request is accepted, add the new friend to online users
      socket.on("friend_accepted", ({ friendInfo }) => {
        if (friendInfo) {
          dispatch(setOnlineUsers({ type: "add", info: friendInfo }));
        }
      });

      socket.on("message_recieved", (newMessage) => {
        // Route to any open mini chat for this sender
        const senderId = getRealtimeMessageSenderId(newMessage);
        const currentUserId = String(user?.userinfo?._id || user?.userinfo?.id || "");
        const isOwnMessage = isRealtimeMessageFromCurrentUser(
          newMessage,
          currentUserId,
        );
        const hasMiniChat = miniChatBus.hasChatForSender(senderId);

        if (
          shouldOpenMiniChatForMessage({
            message: newMessage,
            currentUserId,
            hasMiniChat,
          })
        ) {
          miniChatBus.queueMessage(newMessage);
          miniChatBus.openChat(newMessage.sender);
        }
        miniChatBus.emit(newMessage);

        // Still play sound + show toast when the message opens a background mini chat.
        if (!isOwnMessage && !hasMiniChat) {
          playNotificationSound();
          dispatch(
            reciveNoti({
              type: "message",
            })
          );
          if (msgNoti(newMessage)) {
            toast.custom((t) => (
              <Notification
                t={t}
                toast={toast}
                notification={{
                  sender: newMessage.sender,
                  type: "message",
                  click: `/messages/message/${newMessage.sender?.username || ""}`,
                  // System / unpopulated messages arrive without a sender object.
                  content: `${`${newMessage.sender?.first_name || "Someone"} ${
                    newMessage.sender?.last_name || ""
                  }`.trim()} sent you a message`,
                  createdAt: newMessage.createdAt,
                }}
              />
            ));
          }
        }
        updateMessages(newMessage);
      });

      socket.on("customize_chat", ({ type, content, chat }) => {
        if (type === "theme") {
          updateChatTheme(content, chat);
        }
        if (type === "name") {
          updateChatName(content, chat);
        }
      });

      socket.on("seen", ({ message: newMessage, chat }) => {
        updateSeenMessages(newMessage, chat);
      });

      // ── Incoming call handler ──────────────────────────────────
      // Ring-out: the overlay dismisses itself when nobody answers within the
      // server's 45s window (measured from initiatedAt, so a late-delivered
      // signal does not ring long after the caller gave up).
      const dismissTimedOutCall = (callData, ringMs) => {
        ringTimeoutRef.current = null;
        const ringingCall = incomingCallRef.current;
        const callId = `${callData?.callId || ""}`;
        if (!ringingCall || `${ringingCall.callId}` !== callId) return;
        stopRingtone();
        incomingCallRef.current = null;
        setIncomingCall(null);
        toast(`Missed call from ${getCallerDisplayName(callData?.caller)}`);
        queueSystemLog({
          level: "info",
          category: "call",
          event: "call.incoming.timeout",
          message: "Incoming call rang out without an answer",
          details: { callId, ringMs },
        });
      };

      socket.on("incoming_call", (callData) => {
        // Never ring for a signal that is not for this account, or for our
        // own leg of a call we are placing (2026-09-11 client report: the
        // dialling side saw "<callee> is calling").
        const myId = `${user.userinfo._id || user.userinfo.id || ""}`;
        const callId = `${callData?.callId || ""}`;
        const callerId = `${callData?.caller?._id || callData?.caller?.id || ""}`;
        const recipientId = `${callData?.recipientId || ""}`;
        const ringingCall = incomingCallRef.current;
        const ringingCallId = `${ringingCall?.callId || ""}`;
        let ignoreReason = "";
        if (!callId) ignoreReason = "missing_call_id";
        else if (myId && callerId && callerId === myId) ignoreReason = "self_call";
        else if (myId && recipientId && recipientId !== myId)
          ignoreReason = "not_recipient";
        else if (window.location.pathname.startsWith(`/call/${callId}`))
          ignoreReason = "own_call_page";
        // The same call re-announced (server retry, duplicate routing-room
        // delivery) must not restart the ringtone or the ring-out timer.
        else if (ringingCall && ringingCallId === callId)
          ignoreReason = "already_ringing";
        if (ignoreReason) {
          queueSystemLog({
            level: "warn",
            category: "call",
            event: "call.incoming.ignored",
            message: `Ignored incoming_call (${ignoreReason})`,
            details: { callId, callerId, recipientId, reason: ignoreReason },
          });
          return;
        }
        // A second caller while one call is still ringing: keep the overlay
        // the user is already looking at; the second call rings out on the
        // server side (its caller sees "no answer").
        if (ringingCall) {
          queueSystemLog({
            level: "warn",
            category: "call",
            event: "call.incoming.busy",
            message: "Incoming call arrived while another call was ringing",
            details: { callId, ringingCallId, callerId, recipientId },
          });
          return;
        }
        const ringMs = getRemainingRingMs(callData?.initiatedAt);
        queueSystemLog({
          level: "info",
          category: "call",
          event: "call.incoming.received",
          message: "Incoming call received",
          details: {
            callId,
            roomId: callData?.roomId || "",
            callerId,
            recipientId,
            initiatedAt: callData?.initiatedAt || "",
            ringMs,
            source: "socket",
          },
        });
        setSecondaryCallOffer(null);
        startRingtone(); // continuous ringing until accept/reject/ring-out
        incomingCallRef.current = callData;
        setIncomingCall(callData);
        clearRingTimeout();
        ringTimeoutRef.current = setTimeout(
          () => dismissTimedOutCall(callData, ringMs),
          ringMs,
        );
      });

      const clearIncomingCallIfMatches = (callId) => {
        const activeIncomingCall = incomingCallRef.current;
        if (!activeIncomingCall) return;
        if (
          callId &&
          activeIncomingCall.callId &&
          `${activeIncomingCall.callId}` !== `${callId}`
        ) {
          return;
        }
        clearRingTimeout();
        stopRingtone();
        incomingCallRef.current = null;
        setIncomingCall(null);
      };

      const clearSecondaryCallOfferIfMatches = (callId) => {
        setSecondaryCallOffer((currentOffer) => {
          if (!currentOffer) return currentOffer;
          if (
            callId &&
            currentOffer.callId &&
            `${currentOffer.callId}` !== `${callId}`
          ) {
            return currentOffer;
          }
          return null;
        });
      };

      const handleAnsweredElsewhere = (payload = {}, source = "answered_elsewhere") => {
        const answeredCallId = `${payload.callId || ""}`;
        if (answeredCallId && acceptingCallIdRef.current === answeredCallId) {
          return;
        }
        const activeIncomingCall = incomingCallRef.current;
        const hadIncomingCall = !!activeIncomingCall;
        const currentCallCapabilities = callCapabilitiesRef.current;
        queueSystemLog({
          level: "info",
          category: "call",
          event: "call.answered_elsewhere.received",
          message: "Call answered on another session",
          details: { callId: answeredCallId, wasAlerting: hadIncomingCall, source },
        });
        clearIncomingCallIfMatches(payload.callId);
        if (
          hadIncomingCall &&
          currentCallCapabilities.multiDeviceCallsEnabled &&
          (currentCallCapabilities.callJoinHereTooEnabled ||
            currentCallCapabilities.callMoveHereEnabled)
        ) {
          setSecondaryCallOffer({
            ...activeIncomingCall,
            callId: payload.callId || activeIncomingCall.callId,
            roomId: payload.roomId || activeIncomingCall.roomId,
            answeredBy: payload.answeredBy || null,
            answeredSession: payload.answeredSession || null,
          });
        } else if (hadIncomingCall) {
          toast("Call answered on another device");
        }
      };

      socket.on("call_accepted", (payload = {}) => {
        const callId = `${payload.callId || ""}`;
        const ringingCall = incomingCallRef.current;
        const isAlertingThisCall =
          !!ringingCall && !!callId && `${ringingCall.callId}` === callId;
        // The server addresses call_accepted to both parties' routing rooms,
        // so a tab still ringing for this call learns here that one of the
        // user's other sessions answered: an acceptedSession that is not this
        // tab is "answered elsewhere", not a reason to silently clear.
        if (
          isAlertingThisCall &&
          payload.acceptedSession &&
          !sessionMatchesThisTab(
            payload.acceptedSession,
            getBrowserSessionMetadata(),
          )
        ) {
          handleAnsweredElsewhere(
            {
              callId,
              roomId: payload.roomId,
              answeredBy: payload.recipient || null,
              answeredSession: payload.acceptedSession,
            },
            "call_accepted",
          );
          return;
        }
        clearIncomingCallIfMatches(payload.callId);
      });

      // Bridge call end/reject to the call page through a DOM event: the
      // page's own socket subscription proved unreliable live (2026-09-17 it
      // stayed "in call" after the remote side hung up) while this shell
      // handler always fires for the user's routing room.
      const broadcastCallEvent = (name, payload) => {
        try {
          window.dispatchEvent(new CustomEvent(name, { detail: payload || {} }));
        } catch (_) {}
      };

      socket.on("call_ended", (payload = {}) => {
        const { callId } = payload;
        clearIncomingCallIfMatches(callId);
        clearSecondaryCallOfferIfMatches(callId);
        broadcastCallEvent("nihss:call-ended", payload);
      });

      socket.on("call_rejected", (payload = {}) => {
        const { callId } = payload;
        broadcastCallEvent("nihss:call-rejected", payload);
        const hadIncomingCall = !!incomingCallRef.current;
        clearIncomingCallIfMatches(callId);
        clearSecondaryCallOfferIfMatches(callId);
        // Caller sees this — could show a toast
        if (!hadIncomingCall) {
          toast.error("Call was rejected");
        }
      });

      socket.on("call:answered_elsewhere", (payload = {}) => {
        handleAnsweredElsewhere(payload, "answered_elsewhere");
      });

      mountedSocket.connect();

      return () => {
        // Always tear down THIS instance's socket. When a new shell instance
        // had already replaced the module-level socket, the old one used to
        // stay connected and every user-targeted event (incoming_call,
        // call_ended) then arrived twice — seen live as duplicate
        // call.incoming.received rows on 2026-09-17.
        clearRingTimeout();
        mountedSocket.disconnect();
        if (socket === mountedSocket) {
          socket = undefined;
          notifySocketSubscribers();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop ringtone whenever incomingCall is cleared (safety net)
  useEffect(() => {
    if (!incomingCall) stopRingtone();
  }, [incomingCall, stopRingtone]);

  // Cleanup ringtone on unmount
  useEffect(() => {
    return () => stopRingtone();
  }, [stopRingtone]);

  const {
    notiItems,
    frReqItems,
    msgItems,
    handleOpenBox,
    handleAcceptRequest,
    handleRejectRequest,
  } = useAuthenticatedDropdowns({ user, dispatch });

  // ── Incoming call handlers ──────────────────────────────────────
  const handleAcceptCall = useCallback(
    async () => {
      if (!incomingCall?.callId) return;
      const callId = `${incomingCall.callId}`;
      acceptingCallIdRef.current = callId;
      stopRingtone();
      try {
        const { data } = await axios.post(
          apiUrl(`/api/v1/calls/${callId}/accept`),
          getBrowserSessionMetadata("primary"),
          { withCredentials: true },
        );
        const callPayload = data?.data || {};
        const expiresInSeconds = Number(callPayload.expires_in || 0);
        const joinAuthorization = callPayload.token
          ? {
              callId,
              token: callPayload.token,
              roomId:
                callPayload.room_id || callPayload?.call?.room_id || null,
              expiresAt:
                Date.now() +
                Math.max(0, expiresInSeconds > 0 ? expiresInSeconds : 60) *
                  1000,
              media: callPayload.media || null,
            }
          : null;
        clearRingTimeout();
        incomingCallRef.current = null;
        setIncomingCall(null);
        navigate(`/call/${callId}`, {
          state: joinAuthorization
            ? { callJoinAuthorization: joinAuthorization }
            : undefined,
        });
      } catch (err) {
        // Whatever failed, this tab is no longer the one answering.
        acceptingCallIdRef.current = null;
        const failure = classifyAcceptFailure(err);
        queueSystemLog({
          level: "warn",
          category: "call",
          event: "call.incoming.accept_failed",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to accept call",
          details: {
            callId,
            failure,
            status: Number(err?.response?.status || 0),
          },
        });
        if (failure === "network") {
          // Keep the overlay (and its ring-out timer) so Accept can be
          // tried again once the connection is back.
          toast.error(
            "Could not reach the server. Check your connection and tap Accept again.",
          );
          return;
        }
        clearRingTimeout();
        incomingCallRef.current = null;
        setIncomingCall(null);
        if (failure === "answered_elsewhere") {
          toast("Call answered on another device");
        } else if (failure === "unauthorized") {
          performForcedLogout({ dispatch, socket, reason: "call_accept_401" });
        } else {
          toast.error(err?.response?.data?.message || "Failed to accept call");
        }
      }
    },
    [clearRingTimeout, dispatch, incomingCall, navigate, stopRingtone]
  );

  const handleRejectCall = useCallback(
    async () => {
      if (!incomingCall?.callId) return;
      clearRingTimeout();
      stopRingtone();
      try {
        await axios.post(
          apiUrl(`/api/v1/calls/${incomingCall.callId}/reject`),
          getBrowserSessionMetadata("primary"),
          { withCredentials: true },
        );
      } catch (_) {}
      incomingCallRef.current = null;
      setIncomingCall(null);
    },
    [clearRingTimeout, incomingCall, stopRingtone]
  );

  const handleDismissSecondaryCallOffer = useCallback(() => {
    setSecondaryCallOffer(null);
  }, []);

  const handleJoinSecondaryCall = useCallback(
    (mode) => {
      if (!secondaryCallOffer?.callId) return;
      setSecondaryCallOffer(null);
      navigate(`/call/${secondaryCallOffer.callId}?mode=${mode}`);
    },
    [navigate, secondaryCallOffer]
  );

  // ── Render dropdown items ────────────────────────────────────────
  const renderNotiItem = (n) => {
    const actor = n.actor || n.sender || {};
    const name = `${actor.firstName || actor.first_name || ""} ${actor.lastName || actor.last_name || ""}`.trim() || actor.username || "Someone";
    const photo = actor.photo || "/images/default_pic.png";
    const text = n.message || n.content || `${name} sent you a notification`;
    const link = n.click || `/notifications/all`;
    return (
      <li key={n._id} style={{ borderBottom: "1px solid #eee" }}>
        <Link
          to={link}
          style={{ display: "flex", alignItems: "center", padding: "8px 12px", textDecoration: "none", color: "#333", gap: 8 }}
        >
          <img src={photo} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/default_pic.png"; }} />
          <div style={{ fontSize: 13, lineHeight: 1.3 }}>{text}</div>
        </Link>
      </li>
    );
  };

  const notificationsDropdown =
    notiItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : notiItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {notiItems.map(renderNotiItem)}
      </ul>
    ) : null;

  const friendsDropdown =
    frReqItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : frReqItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {frReqItems.map((r) => {
          const s = r.requester || r.sender || {};
          const senderId = s._id;
          const name = `${s.firstName || s.first_name || ""} ${s.lastName || s.last_name || ""}`.trim() || s.username || "Someone";
          const photo = s.photo || "/images/default_pic.png";
          return (
            <li key={r._id} style={{ borderBottom: "1px solid #eee", padding: "8px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Link to={`/u/${s.username}`} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", color: "#333", flex: 1, minWidth: 0 }}>
                  <img src={photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/default_pic.png"; }} />
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                </Link>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAcceptRequest(senderId); }}
                    title="Accept"
                    style={{
                      width: 32, height: 32, borderRadius: "50%", border: "none",
                      backgroundColor: "#4caf50", color: "#fff", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 700, lineHeight: 1,
                    }}
                  >
                    ✓
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRejectRequest(senderId); }}
                    title="Reject"
                    style={{
                      width: 32, height: 32, borderRadius: "50%", border: "none",
                      backgroundColor: "#e53935", color: "#fff", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 700, lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    ) : null;

  const messagesDropdown =
    msgItems === null ? (
      <div style={{ padding: "10px 12px", color: "#999", fontSize: 13 }}>Loading...</div>
    ) : msgItems.length > 0 ? (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {msgItems.map((c) => {
          // MERN conversations API: participants[] has current user + partner(s)
          const myId = String(user.userinfo?._id || "");
          const other = (c.participants || []).find((p) => String(p._id) !== myId) || c.participants?.[0] || {};
          const name = `${other.first_name || other.firstName || ""} ${other.last_name || other.lastName || ""}`.trim() || other.username || c.name || "Chat";
          const photo = other.photo || "/images/default_pic.png";
          const lastMsg = c.lastMessage;
          const snippet = lastMsg?.content
            ? (lastMsg.content.length > 40 ? lastMsg.content.slice(0, 40) + "…" : lastMsg.content)
            : "";
          const link = `/messages/message/${other.username || c._id}`;
          return (
            <li key={c._id} style={{ borderBottom: "1px solid #eee" }}>
              <Link
                to={link}
                style={{ display: "flex", alignItems: "center", padding: "8px 12px", textDecoration: "none", color: "#333", gap: 8 }}
              >
                <img src={photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                  {snippet && (
                    <div style={{ fontSize: 12, color: "#777", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{snippet}</div>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    ) : null;

  return user.userinfo ? (
    <MiniChatProvider user={user.userinfo}>
      <Topbar user={user.userinfo} onLogout={handleLogout} />
      <SubNavbar
        user={user.userinfo}
        friendRequests={user.userinfo?.recivedRequestsCount || 0}
        messageCount={user.userinfo?.unseenMessages || 0}
        notifCount={user.userinfo?.unseenNotification || 0}
        onOpenBox={handleOpenBox}
        notificationsDropdown={notificationsDropdown}
        friendsDropdown={friendsDropdown}
        messagesDropdown={messagesDropdown}
      />
      <React.Suspense fallback={<Loading />}>
        <Outlet />
      </React.Suspense>
      <Footer />
      <ChatSidebar onlineFriends={onlineUsers} />
      <ChatPopupManager />

      {/* ── Incoming call overlay ─────────────────────────────── */}
      {incomingCall && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Incoming call"
          data-testid="incoming-call-dialog"
          data-call-id={`${incomingCall.callId || ""}`}
          data-call-type={incomingCall.type || "video"}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)", zIndex: 10000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {/* Pulse animation for accept button */}
          <style>{`
            @keyframes vds-pulse-ring {
              0% { box-shadow: 0 0 0 0 rgba(76,175,80,0.6); }
              70% { box-shadow: 0 0 0 16px rgba(76,175,80,0); }
              100% { box-shadow: 0 0 0 0 rgba(76,175,80,0); }
            }
            .vds-call-accept-btn {
              animation: vds-pulse-ring 1.5s ease-out infinite;
            }
          `}</style>
          <div style={{
            backgroundColor: "#fff", borderRadius: 16, padding: "32px 40px",
            textAlign: "center", minWidth: 300, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}>
            <img
              src={incomingCall.caller?.photo || "/images/default_pic.png"}
              alt=""
              style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", marginBottom: 12 }}
            />
            <h3 style={{ margin: "0 0 4px", fontSize: 18 }}>
              {`${incomingCall.caller?.first_name || ""} ${incomingCall.caller?.last_name || ""}`.trim() || "Someone"}
            </h3>
            <p style={{ color: "#666", margin: "0 0 20px", fontSize: 14 }}>
              Incoming {incomingCall.type === "audio" ? "voice" : "video"} call...
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 24 }}>
              <button
                type="button"
                onClick={handleRejectCall}
                data-testid="call-reject"
                aria-label="Reject call"
                style={{
                  width: 56, height: 56, borderRadius: "50%", border: "none",
                  backgroundColor: "#e53935", color: "#fff", fontSize: 22,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                title="Reject"
              >
                <i className="fa-solid fa-phone-slash"></i>
              </button>
              <button
                type="button"
                className="vds-call-accept-btn"
                onClick={handleAcceptCall}
                data-testid="call-accept"
                aria-label="Accept call"
                style={{
                  width: 56, height: 56, borderRadius: "50%", border: "none",
                  backgroundColor: "#4caf50", color: "#fff", fontSize: 22,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                title="Accept"
              >
                <i className="fa-solid fa-phone"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {secondaryCallOffer && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Call answered on another device"
          data-testid="secondary-call-offer"
          data-call-id={`${secondaryCallOffer.callId || ""}`}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.55)", zIndex: 10000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div style={{
            backgroundColor: "#fff", borderRadius: 16, padding: "28px 32px",
            textAlign: "center", width: 360, maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          }}>
            <img
              src={secondaryCallOffer.caller?.photo || "/images/default_pic.png"}
              alt=""
              style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", marginBottom: 12 }}
            />
            <h3 style={{ margin: "0 0 6px", fontSize: 18 }}>
              {`${secondaryCallOffer.caller?.first_name || ""} ${secondaryCallOffer.caller?.last_name || ""}`.trim() || "Someone"}
            </h3>
            <p style={{ color: "#666", margin: "0 0 18px", fontSize: 14, lineHeight: 1.45 }}>
              This call was answered on another device.
              {secondaryCallOffer.answeredBy?.first_name
                ? ` ${secondaryCallOffer.answeredBy.first_name} is already connected.`
                : " You can still join from this browser."}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {callCapabilities.callJoinHereTooEnabled && (
                <button
                  type="button"
                  onClick={() => handleJoinSecondaryCall("companion")}
                  data-testid="call-join-here"
                  style={{
                    border: "none",
                    borderRadius: 10,
                    backgroundColor: "#0A66C2",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    padding: "12px 14px",
                    cursor: "pointer",
                  }}
                >
                  Join Here Too
                </button>
              )}
              {callCapabilities.callMoveHereEnabled && (
                <button
                  type="button"
                  onClick={() => handleJoinSecondaryCall("primary")}
                  data-testid="call-offer-move-here"
                  style={{
                    border: "1px solid rgba(10,102,194,0.25)",
                    borderRadius: 10,
                    backgroundColor: "#EFF6FF",
                    color: "#0A66C2",
                    fontSize: 14,
                    fontWeight: 600,
                    padding: "12px 14px",
                    cursor: "pointer",
                  }}
                >
                  Move Call Here
                </button>
              )}
              <button
                type="button"
                onClick={handleDismissSecondaryCallOffer}
                data-testid="call-offer-close"
                style={{
                  border: "none",
                  borderRadius: 10,
                  backgroundColor: "#f3f4f6",
                  color: "#444",
                  fontSize: 14,
                  fontWeight: 600,
                  padding: "11px 14px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </MiniChatProvider>
  ) : (
    <Navigate to="/login" />
  );
}
